import { useSelector } from 'react-redux';
import { useAppDispatch } from 'redux/store';
import { RootState, CombinedCourse } from 'redux/schemas';
import { Course } from 'redux/schemas/models/course';
import { getCurrentCourse } from 'redux/selectors/course';
import { sendLearnerProgressEmail, sendTestLearnerProgressEmail } from 'redux/actions/learner-progress';
import { LearnerProgressFilters } from './filters-flyout-modal';
import {
  isContactLearnersAvailable,
  buildContactCommunicationFilters,
  resolveViewFilters,
} from './learner-progress-contact-config';

type UseContactLearnersArgs = {
  activeTab: string;
  activeFilters: LearnerProgressFilters | null;
};

const useContactLearners = ({ activeTab, activeFilters }: UseContactLearnersArgs) => {
  const dispatch = useAppDispatch();
  const currentCourse = useSelector<RootState, CombinedCourse>((state) => getCurrentCourse(state)) as Course;
  const savedViews = useSelector((state: RootState) => state.app.learnerProgressDashboard.savedViews);

  const sendContactEmail = (
    communication: { subject: string; body: string },
    push?: { enablePushNotification?: boolean; pushNotificationBody?: string },
  ) => {
    // Unavailable tabs (In Progress / Missed Deadlines) resolve to undefined filters, which would otherwise target every learner.
    if (!isContactLearnersAvailable(activeTab)) {
      return Promise.reject(new Error('Contact Learners is not available for the current view'));
    }
    return dispatch(sendLearnerProgressEmail({
      catalogId: currentCourse.catalogId,
      communication,
      filters: buildContactCommunicationFilters({
        activeTab,
        viewFilters: resolveViewFilters({ activeTab, activeFilters, savedViews }),
        hasUnsavedFilters: !!activeFilters,
      }),
      ...push,
    })).unwrap();
  };

  const sendTestEmail = (
    communication: { subject: string; body: string },
    push?: { enablePushNotification?: boolean; pushNotificationBody?: string },
  ) => dispatch(sendTestLearnerProgressEmail({
    catalogId: currentCourse.catalogId,
    communication,
    ...push,
  })).unwrap();

  return {
    isContactLearnersAvailable: isContactLearnersAvailable(activeTab),
    sendContactEmail,
    sendTestEmail,
  };
};

export default useContactLearners;
